/**
 * Updates the recently viewed products in localStorage.
 *
 * Backwards-compatible storage: legacy entries were strings (just the product
 * ID). New entries are `{ id, handle }` objects. `getProducts()` returns the
 * raw list, `getEntries()` always returns objects (legacy items get a `null`
 * handle which the consumer can handle however it likes).
 */
export class RecentlyViewed {
  /** @static @constant {string} The key used to store the viewed products in session storage */
  static #STORAGE_KEY = 'viewedProducts';
  /** @static @constant {number} The maximum number of products to store */
  static #MAX_PRODUCTS = 12;

  /**
   * Event dispatched on `window` whenever the recently-viewed list changes.
   * Carousels and other consumers can subscribe and re-render their state.
   * @type {string}
   */
  static CHANGE_EVENT = 'recently-viewed:change';

  /**
   * Adds a product to the recently viewed products list.
   *
   * Accepts either a bare productId (backwards compatible with the old
   * call site `RecentlyViewed.addProduct(productId)`) OR a richer object
   * `{ id, handle }` so we can later fetch the product JSON without a
   * search lookup.
   *
   * @param {string | { id: string, handle?: string }} payload
   */
  static addProduct(payload) {
    const id = typeof payload === 'object' ? String(payload?.id ?? '') : String(payload ?? '');
    const handle = typeof payload === 'object' ? (payload?.handle || null) : null;
    if (!id) return;

    const list = this.#readRaw();
    const filtered = list.filter((entry) => this.#entryId(entry) !== id);
    const next = [
      { id, handle: handle || null },
      ...filtered,
    ].slice(0, this.#MAX_PRODUCTS);

    localStorage.setItem(this.#STORAGE_KEY, JSON.stringify(next));
    this.#emit({ action: 'add', id });
  }

  static clearProducts() {
    localStorage.removeItem(this.#STORAGE_KEY);
    this.#emit({ action: 'clear' });
  }

  /**
   * Returns the list of viewed product IDs in newest-first order.
   * Backwards-compatible with the legacy string-array format.
   * @returns {string[]}
   */
  static getProducts() {
    return this.#readRaw().map((entry) => this.#entryId(entry));
  }

  /**
   * Returns the list of viewed products as full {id, handle} objects.
   * Handle is `null` for legacy entries that pre-date the upgrade.
   * @returns {{ id: string, handle: string | null }[]}
   */
  static getEntries() {
    return this.#readRaw().map((entry) => ({
      id: this.#entryId(entry),
      handle: typeof entry === 'object' && entry ? entry.handle ?? null : null,
    }));
  }

  /**
   * @param {unknown} entry
   * @returns {string}
   */
  static #entryId(entry) {
    if (typeof entry === 'string') return entry;
    if (entry && typeof entry === 'object') return String(/** @type {any} */ (entry).id ?? '');
    return '';
  }

  /**
   * @returns {Array<string | { id: string, handle: string | null }>}
   */
  static #readRaw() {
    try {
      const raw = localStorage.getItem(this.#STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (_error) {
      return [];
    }
  }

  /**
   * Dispatches a change event on the window so listeners (e.g. the recently
   * viewed carousel section) can re-render in response.
   * @param {{ action: 'add' | 'clear', id?: string }} detail
   */
  static #emit(detail) {
    try {
      window.dispatchEvent(
        new CustomEvent(this.CHANGE_EVENT, { detail })
      );
    } catch (_error) {
      /* SSR / pre-window contexts — ignore. */
    }
  }
}
